import React from "react";
import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import Form from "@cloudscape-design/components/form";
import FormField from "@cloudscape-design/components/form-field";
import Grid from "@cloudscape-design/components/grid";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { gameStore } from "../stores/games";
import { chestPrizeAddModalStore } from "../stores/chestPrizeAddModal";
import { CHEST_PRIZE_TYPE_OPTIONS } from "../helpers/chestPrizeTypes";

const CHEST_REWARD_OPTIONS = [
	{ value: "money", label: "Money" },
	{ value: "tickets", label: "Tickets" },
	{ value: "freespins", label: "Freespins" },
];

const FREESPINS_PROVIDER_OPTIONS = [{ label: "Endorphina", value: "endorphina" }];

const createChestRewardDraft = () => ({
	id: crypto.randomUUID(),
	rewardType: null,
	rewardValue: "",
	probability: "",
	rewardConditions: {},
	providerSelected: null,
	gameSelected: null,
	betAmount: "",
	expiresAt: "",
});

const parseChestRewardConditions = (value, fallback = {}) => {
	if (Array.isArray(value)) {
		return value;
	}

	if (value && typeof value === "object") {
		return value;
	}

	if (typeof value === "string" && value.trim().length) {
		try {
			return JSON.parse(value);
		} catch (error) {
			return fallback;
		}
	}

	return fallback;
};

const MAX_REWARD_VALUE = "10000000000";
const MAX_REWARD_VALUE_LENGTH = MAX_REWARD_VALUE.length;
const MAX_MONEY_REWARD_VALUE = 100000000;
const MAX_PROBABILITY = 100;
const MAX_PROBABILITY_LENGTH = 6;

const isIntegerStringGreaterThan = (value, maxValue) => {
	const normalized = String(value || "").replace(/^0+(?=\d)/, "");
	const normalizedMax = String(maxValue || "").replace(/^0+(?=\d)/, "");

	if (normalized.length !== normalizedMax.length) {
		return normalized.length > normalizedMax.length;
	}

	return normalized > normalizedMax;
};

const sanitizeIntegerInput = (value) => String(value || "").replace(/\D/g, "");

const sanitizeRewardValueInput = (value) => {
	const sanitized = sanitizeIntegerInput(value).slice(0, MAX_REWARD_VALUE_LENGTH);
	return isIntegerStringGreaterThan(sanitized, MAX_REWARD_VALUE) ? MAX_REWARD_VALUE : sanitized;
};

const sanitizeDecimalInput = (value, decimalPlaces = null) => {
	const normalized = String(value || "").replace(",", ".").replace(/[^\d.]/g, "");
	const [integerPart, ...decimalParts] = normalized.split(".");
	const decimalPart = decimalParts.join("");
	const limitedDecimalPart = decimalPlaces === null ? decimalPart : decimalPart.slice(0, decimalPlaces);
	return decimalParts.length ? `${integerPart}.${limitedDecimalPart}` : integerPart;
};

const sanitizeMoneyRewardValueInput = (value) => {
	const sanitized = sanitizeDecimalInput(value, 2);
	if (!sanitized) {
		return "";
	}

	return Number(sanitized) > MAX_MONEY_REWARD_VALUE ? String(MAX_MONEY_REWARD_VALUE) : sanitized;
};

const sanitizeRewardValueForType = (value, rewardType) =>
	rewardType === "money" ? sanitizeMoneyRewardValueInput(value) : sanitizeRewardValueInput(value);

const sanitizeProbabilityInput = (value) => {
	const sanitized = sanitizeDecimalInput(value).slice(0, MAX_PROBABILITY_LENGTH);
	if (!sanitized) {
		return "";
	}

	return Number(sanitized) > MAX_PROBABILITY ? String(MAX_PROBABILITY) : sanitized;
};

const hasPositiveNumericValue = (value) => /[1-9]/.test(String(value || "").replace(/[^\d]/g, ""));

const isValidIntegerString = (value) => /^\d+$/.test(String(value || ""));

const isValidDecimalString = (value) => /^\d+(\.\d+)?$/.test(String(value || ""));

const buildFreespinRewardConditions = ({ gameSelected, betAmount, expiresAt }) => ({
	gameId: gameSelected?.gameId,
	providerId: gameSelected?.game?.providerID,
	betAmount: sanitizeDecimalInput(betAmount) || "0",
	currency: "RUB",
	expiresAt: parseInt(expiresAt, 10),
});

const buildChestRewardsPayload = (items = []) => ({
	prizes: (items || []).map((item) => {
		const rewardType = item.rewardType?.value || item.rewardType;

		return {
			rewardType,
			rewardValue: Number(sanitizeRewardValueForType(item.rewardValue, rewardType) || "0"),
			probability: Number(sanitizeProbabilityInput(item.probability) || "0"),
			rewardConditions:
				rewardType === "freespins"
					? buildFreespinRewardConditions(item)
					: parseChestRewardConditions(item.rewardConditions),
		};
	}),
});

const validateChestRewards = (items = []) => {
	const chestRewards = items || [];
	if (!chestRewards.length) {
		return "Chest rewards are required";
	}

	for (let index = 0; index < chestRewards.length; index += 1) {
		const reward = chestRewards[index];
		const rewardLabel = `Chest reward #${index + 1}`;
		const rewardType = reward.rewardType?.value || reward.rewardType;

		if (!rewardType) {
			return `${rewardLabel}: reward type is required`;
		}

		const rewardValueIsValid = rewardType === "money"
			? /^\d+(\.\d{0,2})?$/.test(String(reward.rewardValue || ""))
			: isValidIntegerString(reward.rewardValue);

		if (!rewardValueIsValid || !hasPositiveNumericValue(reward.rewardValue)) {
			return `${rewardLabel}: reward value is required`;
		}

		if (rewardType !== "money" && reward.rewardValue.length > MAX_REWARD_VALUE_LENGTH) {
			return `${rewardLabel}: reward value must be no longer than ${MAX_REWARD_VALUE_LENGTH} digits`;
		}

		if (rewardType === "money" && Number(reward.rewardValue) > MAX_MONEY_REWARD_VALUE) {
			return `${rewardLabel}: money reward must be no more than ${MAX_MONEY_REWARD_VALUE} RUB`;
		}

		if (rewardType !== "money" && isIntegerStringGreaterThan(reward.rewardValue, MAX_REWARD_VALUE)) {
			return `${rewardLabel}: reward value must be no more than ${MAX_REWARD_VALUE}`;
		}

		if (reward.probability && !isValidDecimalString(reward.probability)) {
			return `${rewardLabel}: probability must not be negative`;
		}

		if (reward.probability && reward.probability.length > MAX_PROBABILITY_LENGTH) {
			return `${rewardLabel}: probability must be no longer than ${MAX_PROBABILITY_LENGTH} characters`;
		}

		if (reward.probability && Number(reward.probability) > MAX_PROBABILITY) {
			return `${rewardLabel}: probability must be no more than ${MAX_PROBABILITY}`;
		}

		if (rewardType === "freespins") {
			if (!reward.providerSelected) {
				return `${rewardLabel}: provider is required`;
			}
			if (!reward.gameSelected) {
				return `${rewardLabel}: game is required`;
			}
			if (!reward.betAmount) {
				return `${rewardLabel}: bet amount is required`;
			}
			if (!reward.expiresAt) {
				return `${rewardLabel}: expiresAt is required`;
			}

			if (!isValidDecimalString(reward.betAmount) || !hasPositiveNumericValue(reward.betAmount)) {
				return `${rewardLabel}: bet amount must be greater than 0`;
			}
		}
	}

	return "";
};

const getChestRewardsProbabilityTotal = (items = []) =>
	(items || []).reduce((sum, item) => sum + (Number(item.probability) || 0), 0);

const getInitialState = () => ({
	show: false,
	loaded: true,
	name: "",
	type: null,
	typeOptions: CHEST_PRIZE_TYPE_OPTIONS,
	chestRewards: [],
});

class ChestPrizeAddModal extends React.Component {
	getAvailableEndorphinaGames = () => {
		const gameState = this.props.game;
		if (!gameState?.loaded || !gameState?.data) {
			return [];
		}

		return gameState.data
			.filter((game) => game.providerID === 10 && game.active === 1)
			.map((game) => ({
				id: game.gameID,
				description: game.name,
				name: game.name,
				gameID: game.gameID,
				providerID: game.providerID,
			}));
	};

	getChestRewardGames = () =>
		this.getAvailableEndorphinaGames().map((game) => ({
			label: game.description,
			value: game.description,
			gameId: game.id,
			game,
		}));

	onDismiss = () => {
		this.props.setChestPrizeAddModal(getInitialState());
	};

	addChestReward = () => {
		this.props.setChestPrizeAddModal((prevState) => ({
			...prevState,
			chestRewards: [...(prevState.chestRewards || []), createChestRewardDraft()],
		}));
	};

	removeChestReward = (rewardId) => {
		this.props.setChestPrizeAddModal((prevState) => ({
			...prevState,
			chestRewards: (prevState.chestRewards || []).filter((item) => item.id !== rewardId),
		}));
	};

	updateChestReward = (rewardId, patch) => {
		this.props.setChestPrizeAddModal((prevState) => ({
			...prevState,
			chestRewards: (prevState.chestRewards || []).map((item) =>
				item.id === rewardId ? { ...item, ...patch } : item
			),
		}));
	};

	handleChestRewardTypeChange = (rewardId, rewardType) => {
		this.updateChestReward(rewardId, {
			rewardType,
			rewardValue: "",
			probability: "",
			rewardConditions: {},
			providerSelected: null,
			gameSelected: null,
			betAmount: "",
			expiresAt: "",
		});
	};

	renderChestRewardsEditor = () => {
		const chestRewards = this.props.chestPrizeAddModal.chestRewards || [];

		return (
			<Container
				header={
					<Header
						actions={
							<Button variant="primary" onClick={this.addChestReward}>
								Add chest reward
							</Button>
						}
					>
						Chest configuration
					</Header>
				}
			>
				<SpaceBetween direction="vertical" size="l">
					{!chestRewards.length && <Alert type="info">Add at least one reward to the chest pool.</Alert>}

					{chestRewards.map((reward, index) => {
						const chestGames = reward.providerSelected ? this.getChestRewardGames(reward) : [];

						return (
							<Container
								key={reward.id}
								header={
									<Header
										actions={
											<Button variant="normal" onClick={() => this.removeChestReward(reward.id)}>
												Remove
											</Button>
										}
									>
										{`Chest reward #${index + 1}`}
									</Header>
								}
							>
								<SpaceBetween direction="vertical" size="m">
									<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
										<FormField label="Reward type" stretch>
											<Select
												selectedOption={reward.rewardType}
												onChange={({ detail }) => this.handleChestRewardTypeChange(reward.id, detail.selectedOption)}
												options={CHEST_REWARD_OPTIONS}
												placeholder="Select reward type"
											/>
										</FormField>

										<FormField label={reward.rewardType?.value === "money" ? "Reward value (RUB)" : "Reward value"} stretch>
											<Input
												inputMode={reward.rewardType?.value === "money" ? "decimal" : "numeric"}
												placeholder="Enter reward value"
												value={reward.rewardValue}
												onChange={(event) =>
													this.updateChestReward(reward.id, {
														rewardValue: sanitizeRewardValueForType(event.detail.value, reward.rewardType?.value),
													})
												}
											/>
										</FormField>

										<FormField label="Probability" stretch>
											<Input
												inputMode="decimal"
												placeholder="Enter probability"
												value={reward.probability}
												onChange={(event) => this.updateChestReward(reward.id, { probability: sanitizeProbabilityInput(event.detail.value) })}
											/>
										</FormField>
									</Grid>

									{reward.rewardType?.value === "freespins" && (
										<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }]}>
											<FormField label="Provider" stretch>
												<Select
													filteringType="auto"
													placeholder="Select provider"
													selectedOption={reward.providerSelected}
													onChange={({ detail }) =>
														this.updateChestReward(reward.id, {
															providerSelected: detail.selectedOption,
															gameSelected: null,
														})
													}
													options={FREESPINS_PROVIDER_OPTIONS}
												/>
											</FormField>

											{reward.providerSelected && (
												<>
													<FormField label="Game" stretch>
														<Select
															filteringType="auto"
															placeholder="Select game"
															selectedOption={reward.gameSelected}
															onChange={({ detail }) => this.updateChestReward(reward.id, { gameSelected: detail.selectedOption })}
															options={chestGames}
														/>
													</FormField>

													<FormField label="Bet amount" stretch>
														<Input
															inputMode="decimal"
															placeholder="Enter bet amount"
															value={reward.betAmount || ""}
															onChange={(event) => this.updateChestReward(reward.id, { betAmount: sanitizeDecimalInput(event.detail.value) })}
														/>
													</FormField>

													<FormField label="Expiration date" stretch>
														<Input
															placeholder="Enter expiration date"
															value={reward.expiresAt || ""}
															onChange={(event) => this.updateChestReward(reward.id, { expiresAt: event.detail.value })}
														/>
													</FormField>
												</>
											)}
										</Grid>
									)}
								</SpaceBetween>
							</Container>
						);
					})}

					{chestRewards.length > 0 && (
						<Box variant="small">{`Total probability: ${getChestRewardsProbabilityTotal(chestRewards)}`}</Box>
					)}
				</SpaceBetween>
			</Container>
		);
	};

	validation = () => {
		if (!this.props.chestPrizeAddModal.name?.trim()) {
			return "Name is required";
		}

		if (!this.props.chestPrizeAddModal.type?.value) {
			return "Type is required";
		}

		return validateChestRewards(this.props.chestPrizeAddModal.chestRewards || []);
	};

	addChestPrize = async () => {
		try {
			this.props.setChestPrizeAddModal({
				...this.props.chestPrizeAddModal,
				loaded: false,
			});

			const validation = this.validation();
			if (validation?.length) {
				this.props.Interface.addNoty("Error", validation);
				this.props.setChestPrizeAddModal({
					...this.props.chestPrizeAddModal,
					loaded: true,
				});
				return;
			}

			const payload = {
				name: this.props.chestPrizeAddModal.name.trim(),
				type: this.props.chestPrizeAddModal.type.value,
				rewardConditions: buildChestRewardsPayload(this.props.chestPrizeAddModal.chestRewards || []),
			};

			await this.props.Interface.request("set", { class: "data", module: "chestPrize", method: "add" }, payload);

			this.props.Interface.addNoty("Success", `Chest "${payload.name}" was successfully added`);
			this.onDismiss();
			this.props.Interface.loadChestPrizeList();
		} catch (error) {
			console.error(error);
			this.props.Interface.addNoty("Error", "Failed to add chest");
			this.props.setChestPrizeAddModal({
				...this.props.chestPrizeAddModal,
				loaded: true,
			});
		}
	};

	render() {
		return (
			<Modal
				onDismiss={this.onDismiss}
				visible={this.props.chestPrizeAddModal.show}
				size="max"
				header="Add chest"
				footer={
					<SpaceBetween direction="horizontal" size="xs">
						<Button variant="link" onClick={this.onDismiss}>
							Cancel
						</Button>
						<Button
							variant="primary"
							loading={!this.props.chestPrizeAddModal.loaded}
							onClick={this.addChestPrize}
						>
							Create chest
						</Button>
					</SpaceBetween>
				}
			>
				{!this.props.chestPrizeAddModal.loaded && <Spinner size="large" />}

				<Form>
					<SpaceBetween direction="vertical" size="l">
						<FormField label="Name" stretch>
							<Input
								placeholder="Enter chest name"
								value={this.props.chestPrizeAddModal.name}
								onChange={({ detail }) =>
									this.props.setChestPrizeAddModal({
										...this.props.chestPrizeAddModal,
										name: detail.value,
									})
								}
							/>
						</FormField>

						<FormField label="Type" stretch>
							<Select
								selectedOption={this.props.chestPrizeAddModal.type}
								onChange={({ detail }) =>
									this.props.setChestPrizeAddModal({
										...this.props.chestPrizeAddModal,
										type: detail.selectedOption,
									})
								}
								options={this.props.chestPrizeAddModal.typeOptions}
								placeholder="Select chest type"
							/>
						</FormField>

						{this.renderChestRewardsEditor()}
					</SpaceBetween>
				</Form>
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const chestPrizeAddModal = useRecoilValue(chestPrizeAddModalStore);
		const setChestPrizeAddModal = useSetRecoilState(chestPrizeAddModalStore);
		const game = useRecoilValue(gameStore);

		return (
			<Component
				{...props}
				chestPrizeAddModal={chestPrizeAddModal}
				setChestPrizeAddModal={setChestPrizeAddModal}
				game={game}
			/>
		);
	};
}

export default withRecoil(ChestPrizeAddModal);
